import React, { useState, useRef, useLayoutEffect } from 'react';

import useInView from 'react-cool-inview';

import { useMediaQuery } from 'react-responsive';

import { PageMenu } from '@ui/moleculs/PageMenu';
import { BlockArrow } from '@ui/moleculs/BlockArrow';

import { BlockInfo } from '@ui/moleculs/BlockInfo';
import { BlockColoredText } from '@ui/moleculs/BlockColoredText';
import { BlockScrollText } from '@ui/moleculs/BlockScrollText';

import { BlockVideo } from '@ui/moleculs/videos/BlockVideo';
import { BlockVideo2 } from '@ui/moleculs/videos/BlockVideo2';
import { BlockVideo3 } from '@ui/moleculs/videos/BlockVideo3';
import { BlockVideo4 } from '@ui/moleculs/videos/BlockVideo4';

import { Person } from '@ui/moleculs/persons/Person';

import { PageHeader } from '@ui/moleculs/PageHeader';
import { PageFooter } from '@ui/moleculs/PageFooter';

type InViewTypes = {
  id?: string;
  scrollDir?: string;
};

const InView: React.FC<InViewTypes> = ({ children, ...props }) => {
  const { scrollDir, ...rest } = props;

  const {
    observe
  } = useInView<HTMLDivElement>({
    threshold: 0.42, // Default is 0
    onChange: ({ observe, unobserve }) => {
      unobserve();
      observe();
    },
    onEnter: ({ entry }) => {
      const { target } = entry;
      if (scrollDir === 'down') {
        target.classList.add('on');
      }
      const parent = document.getElementById('root');
      parent && parent.classList.add('on');
    },
    onLeave: ({ entry }) => {
      const { target } = entry;
      if (scrollDir === 'up') {
        target.classList.remove('on');
      }
      const parent = document.getElementById('root');
      parent && parent.classList.remove('on');
    },
  });
  return (
    <div ref={observe} {...rest}>
      {children}
    </div>
  );
}

const App: React.FC = () => {
  const [natureActive, changeNatureActive] = useState(true);

  const blockVideo3 = useRef<HTMLDivElement>(null);

  const toggleNatureActive = (bool?: boolean | undefined) =>
    changeNatureActive(typeof bool !== 'undefined' ? bool : !natureActive);

  const isDesktopOrLaptop = useMediaQuery({
    query: '(min-width: 1024px)'
  });

  const [scrollDir, setScrollDir] = useState('down');

  useLayoutEffect(() => {
    const threshold = 0;
    let lastScrollY = window.pageYOffset;
    let ticking = false;
  
    const updateScrollDir = () => {
      const scrollY = window.pageYOffset;
  
      if (Math.abs(scrollY - lastScrollY) <= threshold) {
        ticking = false;
        return;
      }
      setScrollDir(scrollY > lastScrollY ? 'down' : 'up');
      lastScrollY = scrollY > 0 ? scrollY : 0;
      ticking = false;
    };

    const updatePosition = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDir);
        ticking = true;
      }
    }
    window.addEventListener('scroll', updatePosition);
    updatePosition();
    return () => window.removeEventListener('scroll', updatePosition);
  }, []);

  return (
    <>
      <PageMenu isDesktopOrLaptop={isDesktopOrLaptop} />
      
      <PageHeader active={natureActive} onClick={toggleNatureActive} isDesktopOrLaptop={isDesktopOrLaptop} />

      <BlockArrow active={natureActive} onClick={toggleNatureActive} isDesktopOrLaptop={isDesktopOrLaptop} />

      <BlockInfo
        isDesktopOrLaptop={isDesktopOrLaptop}
        active={natureActive}
        onClick={toggleNatureActive}
        headerLeft="Коронавирус «сбежал» из лаборатории"
        headerRight="Большинство коронавирусов возникло в среде животных"
        index={0}
      />

      <BlockScrollText
        isDesktopOrLaptop={isDesktopOrLaptop}
      />

      <InView id="video-1" scrollDir={scrollDir}>
        <BlockColoredText
          isDesktopOrLaptop={isDesktopOrLaptop}
          active={natureActive}
          onClick={toggleNatureActive}
          index={1}
          headerLeft="Из лаборатории"
          textLeft="С коронавирусами и особенно их S -белком давно экспериментировали"
          headerRight="Из природы"
          textRight="Природа экспериментирует с вирусами намного больше, нежели это делают ученые в лаборатории"
        />
        <BlockVideo
          active={natureActive}
          onClick={toggleNatureActive}
          isDesktopOrLaptop={isDesktopOrLaptop}
        />
      </InView>

      <BlockInfo
        isDesktopOrLaptop={isDesktopOrLaptop}
        active={natureActive}
        onClick={toggleNatureActive}
        headerLeft="Пандемия началась в Ухане, где находится Вирусологическая лаборатория для самых опасных патогенов и где работают с коронавирусами"
        headerRight="Вирус идентичный на 96.4%: есть у летучих мышей. Он значится в базе под названием RatG13"
      />

      <InView id="video-2" scrollDir={scrollDir}>
        <BlockColoredText
          index={2}
          isDesktopOrLaptop={isDesktopOrLaptop}
          active={natureActive}
          onClick={toggleNatureActive}
          headerLeft="Из лаборатории"
          textLeft="В Уханьском институте расположена самая большая коллекция коронавирусов и там велась работа по увеличению их патогенности"
          headerRight="Из природы"
          textRight="В каждой летучей мыши живет несколько коронавирусов и они активно обмениваются генетическим материалом."
        />
        <BlockVideo2
          active={natureActive}
          onClick={toggleNatureActive}
          isDesktopOrLaptop={isDesktopOrLaptop}
        />
      </InView>

      <BlockInfo
        isDesktopOrLaptop={isDesktopOrLaptop}
        active={natureActive}
        onClick={toggleNatureActive}
        headerLeft="Летучие мыши,у которых нашли родственный вирус, водятся в 3000 км от Уханя"
        headerRight="Подозрительная фуриновая вставка есть и у других вирусов"
      />

      <InView id="video-3" scrollDir={scrollDir}>
        <BlockColoredText
          index={3}
          isDesktopOrLaptop={isDesktopOrLaptop}
          active={natureActive}
          onClick={toggleNatureActive}
          headerLeft="Из лаборатории"
          textLeft="Даже событие самой низкой вероятности может произойти"
          headerRight="Из природы"
          textRight="Где бы не появился вирус в Китае, везде рядом можно найти лабораторию, из которой она могла бы произойти"
        />
        <BlockVideo3
          active={natureActive}
          onClick={toggleNatureActive}
          isDesktopOrLaptop={isDesktopOrLaptop}
        />
      
        <BlockVideo4
          active={natureActive}
          onClick={toggleNatureActive}
          isDesktopOrLaptop={isDesktopOrLaptop}
        />
      </InView>

      <InView id="person-1" scrollDir={scrollDir}>
        <Person
          headerLeft="Из лаборатории"
          headerRight="Из природы"
          active={natureActive}
          onClick={toggleNatureActive}
          isDesktopOrLaptop={isDesktopOrLaptop}
        />
      </InView>

      <PageFooter />
    </>
  );
}

export default App;
